import React from "react";
import { Container, Grid, Box, Typography, Divider, Link as MuiLink, List, ListItem } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import FormNewsletter from "@forms/newsletter.form";
// @ts-ignore
import LogoText from "@logo/vld-2026.png";
import Socials from "../footer/socials.footer";
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";

const Footer: React.FC = () => {
    const { trackEvent } = useMatomo();

    // Tracking des liens internes (Router)
    const handleInternalLinkClick = (linkName: string, destination: string) => {
        trackEvent({
            category: 'Footer',
            action: 'Click',
            name: linkName,
        });
    };

    // Tracking des liens externes
    const handleExternalLinkClick = (linkName: string, url: string) => {
        trackEvent({
            category: 'Footer',
            action: 'Click External',
            name: linkName,
        });
    };

    // Style commun pour les titres de sections du footer
    const sectionTitleStyle = {
        variant: "subtitle2",
        fontWeight: 700,
        color: "text.secondary",
        sx: { textTransform: "uppercase", letterSpacing: 1, mb: 2 }
    } as const;

    // Helper pour les liens stylisés
    const FooterLink = ({ to, href, label, onClick }: { to?: string, href?: string, label: string, onClick?: () => void }) => {
        const content = (
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "0.7rem", marginRight: 8, opacity: 0.6 }} />
                <Typography variant={"subtitle2"}>{label}</Typography>
            </Box>
        );

        const commonSx = {
            textDecoration: "none",
            color: "text.primary",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s ease",
            "&:hover": {
                color: "primary.main",
                transform: "translateX(4px)"
            }
        };

        if (to) {
            return (
                <MuiLink component={RouterLink} to={to} onClick={onClick} sx={commonSx}>
                    {content}
                </MuiLink>
            );
        }
        return (
            <MuiLink href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} sx={commonSx}>
                {content}
            </MuiLink>
        );
    };

    return (
        <Box sx={{ width: "100vw", bgcolor: "#f8f9fa", borderTop: "1px solid", borderColor: "divider" }}>
            <Container sx={{ pb: 6, pt: 8 }}>
                <Grid container spacing={6}>

                    {/* Colonne 1 : Réseaux Sociaux (Le titre est inclus dans le composant) */}
                    <Grid size={{ xs: 12, md: 3}}>
                        <Socials />
                    </Grid>

                    {/* Colonne 2 : Newsletter */}
                    <Grid size={{ xs: 12, md: 5}}>
                        <Typography {...sectionTitleStyle}>
                            Restez informé
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                            Recevez directement dans votre boîte mail l'actualité de la campagne,
                            les temps forts et les actions concrètes de VLD pour Bois-le-Roi.
                        </Typography>
                        <FormNewsletter />
                    </Grid>

                    {/* Colonne 3 : Liens utiles */}
                    <Grid size={{ xs: 12, md: 4}}>
                        <Typography {...sectionTitleStyle}>
                            Nous rejoindre
                        </Typography>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ px: 0, py: 0.8 }}>
                                <FooterLink
                                    to="/vld-2026/faire-un-don"
                                    label="Je soutiens la campagne VLD 2026"
                                    onClick={() => handleInternalLinkClick('Soutenir Campagne VLD 2026', '/vld-2026/faire-un-don')}
                                />
                            </ListItem>
                            <ListItem sx={{ px: 0, py: 0.8 }}>
                                <FooterLink
                                    href="https://www.helloasso.com/associations/vivre-la-democratie-a-blr/adhesions/bulletin-d-adhesion-a-l-association-vivre-la-democratie-a-blr-1"
                                    label="J'adhère à l'association VLD"
                                    onClick={() => handleExternalLinkClick('Adhésion HelloAsso', 'https://www.helloasso.com/associations/vivre-la-democratie-a-blr/adhesions/bulletin-d-adhesion-a-l-association-vivre-la-democratie-a-blr-1')}
                                />
                            </ListItem>
                            <ListItem sx={{ px: 0, py: 0.8 }}>
                                <FooterLink
                                    href="https://www.helloasso.com/associations/vivre-la-democratie-a-blr"
                                    label="Je fais un don à l'association"
                                    onClick={() => handleExternalLinkClick('Don HelloAsso', 'https://www.helloasso.com/associations/vivre-la-democratie-a-blr')}
                                />
                            </ListItem>
                            <ListItem sx={{ px: 0, py: 0.8 }}>
                                <FooterLink
                                    to="/contactez-nous"
                                    label="Contacter l'équipe"
                                    onClick={() => handleInternalLinkClick('Contact Footer', '/contactez-nous')}
                                />
                            </ListItem>
                        </List>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 6, opacity: 0.6 }} />

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                    }}
                >
                    <Typography variant="overline" sx={{ fontSize: "1rem", fontWeight: 700, color: "text.primary" }}>
                        VIVRE LA DÉMOCRATIE À BOIS-LE-ROI © 2026
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        18 RUE GUIDO SIGRISTE - 77590 BOIS-LE-ROI
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Association loi 1901 – Tous droits réservés
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;